/**
 * [INPUT]: 依赖 cartesian context 的 series 注册/数据带与 SeriesContext
 * [OUTPUT]: 对外提供 Area/Line series，声明 dataKey、纹理、线宽/辉光宽/清晰度与可点击选择
 * [POS]: 连续图/混合图根与 Dot 子件之间的 series 语义桥，Canvas 完成实际绘制
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { useEffect } from "react";
import { useChartPart } from "./chart-context";
import { SeriesContext } from "./series-context"

/**
 * Shared implementation for the continuous series (`<Area>`, `<Line>`). The
 * dithered fill/line is painted on the canvas; this registers the series so the
 * canvas knows how to draw it, wires click-to-select via a transparent band
 * polygon, and exposes the series to child `<Dot>`/`<ActiveDot>` markers.
 */
function CartesianSeries({
  part,
  kind,
  dataKey,
  variant = "gradient",
  strokeVariant = "solid",
  glowWidth,
  strokeWidth,
  strokeAlpha,
  isClickable = false,
  children
}) {
  const ctx = useChartPart(part, kind === "line" ? ["line", "combo"] : ["area", "combo"])
  const { registerSeries, unregisterSeries } = ctx

  if (import.meta.env.DEV && !ctx.config[dataKey]) {
    console.warn(
      `<${part} dataKey="${dataKey}" />: "${dataKey}" is not in the chart \`config\`. Add it so the series has a colour and label.`
    )
  }

  useEffect(() => {
    registerSeries({ dataKey, kind, variant, strokeVariant, glowWidth, strokeWidth, strokeAlpha })
    return () => unregisterSeries(dataKey);
  }, [dataKey, kind, variant, strokeVariant, glowWidth, strokeWidth, strokeAlpha, registerSeries, unregisterSeries])

  const band = ctx.bands[dataKey]
  if (!ctx.ready || !band) return null

  const seed = ctx.seedOf(dataKey)
  const emphasis = ctx.selectedDataKey ?? ctx.focusDataKey
  const dimmed = emphasis !== null && emphasis !== dataKey
  const onClick = isClickable
    ? () => ctx.selectDataKey(ctx.selectedDataKey === dataKey ? null : dataKey)
    : undefined

  // Transparent hit polygon tracing the series' own band, so clicking a series
  // selects *that* series. The Legend offers the same toggle accessibly.
  // One pass out along the top edge, one pass back along the floor.
  let hitPath = null
  if (isClickable) {
    const parts = []
    band.forEach((b, i) => {
      parts.push(`${i === 0 ? "M" : "L"}${ctx.xCenter(i)},${ctx.yFor(dataKey, b[1])}`)
    })
    for (let i = band.length - 1; i >= 0; i -= 1) {
      parts.push(`L${ctx.xCenter(i)},${ctx.yFor(dataKey, band[i][0])}`)
    }
    hitPath = `${parts.join(" ")} Z`
  }

  return (
    <>
      {hitPath && (
        // biome-ignore lint/a11y/noStaticElementInteractions: progressive enhancement; the Legend offers the same toggle accessibly
        (<path
          d={hitPath}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={onClick} />)
      )}
      <SeriesContext value={{ dataKey, seed, dimmed }}>
        {children}
      </SeriesContext>
    </>
  );
}

/** One area series — dithered fill from the value line down to its floor. */
export function Area(props) {
  return <CartesianSeries part="Area" kind="area" {...props} />;
}

/** One line series — bright line with a thin dither glow hugging it. */
export function Line(props) {
  return <CartesianSeries part="Line" kind="line" {...props} />;
}
